import React from 'react';
import { useCurrency } from '../contexts/CurrencyContext';
import { getCurrencyOptions } from '../utils/currencyUtils';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { Link as RouterLink } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import goldColors from '../themes/goldColors';
import PaymentMethodsBadges from './PaymentMethodsBadges';
import DarwinOptimaLogo from './DarwinOptimaLogo';

const PreciosPage = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  // 17 sep 2026 (Augusto: "Elimina plan negocios. Eso lo manejamos con
  // Capital Optima"): PCM queda solo para personas. Los planes Negocio
  // Básico / Negocio Pro y la pestaña "Para Empresas" salieron; la oferta
  // para empresas es CapitalOptima (capo.darwinoptima.com).
  const { currency, changeCurrency, convertAndFormat } = useCurrency();
  const currencyOptions = getCurrencyOptions();

  // Handle currency change
  const handleCurrencyChange = (event) => {
    changeCurrency(event.target.value);
  };

  // 4 ago 2026: convertAndFormat usa un tipo de cambio EN VIVO (API
  // externa, ver CurrencyContext.js) solo para mostrar un estimado -- pero
  // el cobro real en soles vía Mercado Pago usa un precio FIJO
  // (PLAN_PRICE_*_PEN en config.py). Con el tipo de cambio del día, esos
  // dos números no coinciden (ej. se veía "PEN 33.77" acá cuando en
  // realidad se cobran S/37) -- lo cual es confuso para alguien comparando
  // lo que ve con lo que le cobran. Cuando la moneda elegida es PEN, se
  // muestra el precio fijo real en vez de la conversión; para cualquier
  // otra moneda (que no se cobra directo, solo USD/PEN existen como
  // pasarela) sigue siendo un estimado con tipo de cambio en vivo, que es
  // lo mejor que se puede ofrecer sin una pasarela en esa moneda.
  const formatFixedPEN = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const displayPrice = (plan) => {
    if (currency === 'PEN' && plan.pricePEN != null) return formatFixedPEN(plan.pricePEN);
    return convertAndFormat(plan.priceUSD);
  };

  // Pricing plans data — 2 planes del aplicativo (misma estructura que
  // HomePage.js). La consultoría 1:1 ya no se vende acá, ver el banner de
  // Darwin Optima más abajo.
  const plans = [
    {
      title: language === 'es' ? 'Personal' : 'Personal',
      priceUSD: 9.99,
      pricePEN: 37,
      period: language === 'es' ? 'por mes' : 'per month',
      description: language === 'es'
        ? 'Para individuos que buscan gestionar sus finanzas personales básicas'
        : 'For individuals looking to manage basic personal finances',
      features: [
        {
          included: true,
          text: language === 'es' ? 'Asistente de configuración inicial' : 'Initial setup assistant'
        },
        {
          included: true,
          text: language === 'es' ? 'Ingreso manual de transacciones' : 'Manual transaction entry'
        },
        {
          included: true,
          text: language === 'es' ? 'Calendario de flujos de caja' : 'Cash flow calendar'
        },
        {
          included: true,
          text: language === 'es' ? 'Categorización de ingresos y egresos' : 'Income and expense categorization'
        },
        {
          included: true,
          text: language === 'es' ? 'Herramientas de gestión de deudas' : 'Debt management tools'
        },
        {
          included: true,
          text: language === 'es' ? 'Informes básicos' : 'Basic reports'
        },
        {
          // 4 ago 2026: 3 diferenciadores gratis (sin IA, sin costo de
          // procesamiento) pedidos por Augusto para el plan Personal --
          // ver bulk_import.py, transactions.py (duplicate-last-month) y
          // debts.py (reminders/send) en el backend.
          included: true,
          text: language === 'es'
            ? 'Importador de CSV/Excel sin costo adicional'
            : 'Free CSV/Excel importer'
        },
        {
          included: true,
          text: language === 'es' ? 'Recordatorios de vencimiento de deudas por correo (los activas tú)' : 'Debt due-date email reminders (you turn them on)'
        },
        {
          included: false,
          text: language === 'es' ? 'Alertas y reportes con IA' : 'AI alerts and reports'
        },
        {
          included: false,
          text: language === 'es' ? 'Ingreso de transacciones multi canal' : 'Multi-channel transaction entry'
        },
      ],
      buttonText: language === 'es' ? 'Prueba 14 días gratis' : 'Try 14 Days Free',
      color: theme.palette.primary.main,
      buttonVariant: 'outlined'
    },
    {
      title: language === 'es' ? 'Pro' : 'Pro',
      // 30 ago 2026 (pedido de Augusto: "aun no está a la venta así que
      // podemos subir precios sin que nadie se queje"): subido de $25/S/93
      // a $29/S/108 -- cubre el aumento de costo de Sonnet 5 (+50% desde
      // el 1 sep) y el nuevo constructor de presupuesto con IA. Ver
      // PLAN_PRICE_PRO_USD/PEN en backend_new/app/core/config.py, que es
      // la fuente real que usa Subscription.js (esta página es solo el
      // copy público de /precios, no lee el precio del backend).
      priceUSD: 29,
      pricePEN: 108,
      period: language === 'es' ? 'por mes' : 'per month',
      description: language === 'es'
        ? 'Para usuarios que desean funciones avanzadas asistidas por IA'
        : 'For users who want advanced, AI-assisted features',
      features: [
        {
          included: true,
          text: language === 'es' ? 'Todo lo de Personal' : 'Everything in Personal'
        },
        {
          // 4 ago 2026 (pedido de Augusto): el importador de estados de
          // cuenta se fusiona dentro de esta misma línea, justo después
          // de "ingreso multi canal" -- antes vivía como bullet aparte,
          // más abajo en la lista.
          included: true,
          text: language === 'es'
            ? 'Ingreso de transacciones multi canal (WhatsApp, voz) e importador de estados de cuenta con IA'
            : 'Multi-channel transaction entry (WhatsApp, voice) and AI statement importer'
        },
        {
          included: true,
          text: language === 'es' ? 'Auto-categorización y alertas con IA' : 'AI auto-categorization and alerts'
        },
        {
          included: true,
          text: language === 'es' ? 'Reportes inteligentes con IA' : 'AI-powered smart reports'
        },
        {
          // 28 ago 2026 (pedido de Augusto): "Pregúntale a la IA" -- ver
          // backend_new/.../reports.py::ask_report y Reports.js. Comparte
          // la misma cuota de páginas que el importador de estados de
          // cuenta (ver bullet de abajo), no es una cuota aparte.
          included: true,
          text: language === 'es'
            ? 'Pregúntale a la IA: preguntas libres sobre tus finanzas, con gráficos'
            : 'Ask the AI: free-form questions about your finances, with charts'
        },
        {
          // 29 ago 2026 (pedido de Augusto: "necesito exista una opción IA
          // para que construya el presupuesto... ser parte del paquete
          // premium") -- ver backend_new/.../budgets.py::ai_suggest_budget.
          // También consume páginas de la misma cuota, más caro por
          // corrida (5 páginas) porque analiza varios meses de historial.
          included: true,
          text: language === 'es'
            ? 'Constructor de presupuesto con IA: te sugiere cuánto gastar por categoría y qué recortar'
            : 'AI budget builder: suggests how much to spend per category and what to cut'
        },
        {
          // 4 ago 2026: cuota real de páginas del plan (ver
          // backend_new/app/core/plans.py, PLAN_PAGE_QUOTA['pro'] = 150) --
          // se puede comprar más si se necesita (paquetes de páginas
          // extra, disponibles desde el importador dentro de la app).
          included: true,
          text: language === 'es'
            ? '150 páginas de estados de cuenta al mes (puedes comprar más si necesitas)'
            : '150 statement pages per month (buy more if you need them)'
        },
        {
          included: true,
          text: language === 'es' ? 'Soporte por correo electrónico' : 'Email support'
        },
      ],
      buttonText: language === 'es' ? 'Prueba 14 días gratis' : 'Try 14 Days Free',
      color: goldColors.main,
      recommended: true,
      buttonVariant: 'contained'
    }
  ];

  const activePlans = plans;

  // Asesoría 1:1 suelta — solo tiene sentido para individuos (Para
  // Personas). Para negocios, la oferta real es Darwin Optima completa
  // (reordenamiento a fondo, no una sesión suelta), ver el banner debajo
  // del tab "Para Empresas".
  const personalConsulting = {
    title: language === 'es' ? 'Asesoría Personal' : 'Personal Consulting',
    description: language === 'es'
      ? 'Sesión personalizada uno a uno con un especialista para ayudarte a ordenar tus finanzas personales.'
      : 'One-on-one personalized session with a specialist to help you organize your personal finances.',
    priceUSD: 45,
    period: language === 'es' ? 'por sesión' : 'per session',
    features: [
      language === 'es' ? 'Sesión personalizada de 60 minutos' : '60-minute personalized session',
      language === 'es' ? 'Diagnóstico de tu situación financiera actual' : 'Current financial situation diagnosis',
      language === 'es' ? 'Plan de acción personalizado' : 'Customized action plan',
      language === 'es' ? 'Seguimiento por email' : 'Email follow-up',
    ],
  };

  const darwinOptimaUrl = 'https://darwinoptima.com';

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        {/* Currency Selector */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
          <FormControl variant="outlined" size="small">
            <InputLabel id="currency-selector-label">
              {language === 'es' ? 'Moneda' : 'Currency'}
            </InputLabel>
            <Select
              labelId="currency-selector-label"
              id="currency-selector"
              value={currency}
              onChange={handleCurrencyChange}
              label={language === 'es' ? 'Moneda' : 'Currency'}
              sx={{ minWidth: 150 }}
            >
              {currencyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            fontWeight="bold"
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${goldColors.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 3
            }}
          >
            {language === 'es' ? 'Planes y Precios' : 'Plans & Pricing'}
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ maxWidth: 800, mx: 'auto', mb: 3 }}
          >
            {language === 'es'
              ? 'Elija el plan que mejor se adapte a sus necesidades financieras. Todos los planes incluyen acceso a nuestra aplicación innovadora.'
              : 'Choose the plan that best suits your financial needs. All plans include access to our innovative app.'}
          </Typography>

        </Box>

        {/* Pricing Plans */}
        <Box sx={{ mb: 10 }}>
          <Grid container spacing={4} justifyContent="center">
            {activePlans.map((plan, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card 
                  elevation={plan.recommended ? 8 : 2} 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: 6
                    },
                    ...(plan.recommended && {
                      borderTop: `5px solid ${plan.color}`,
                    })
                  }}
                >
                  {plan.recommended && (
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: -12, 
                        right: 20, 
                        bgcolor: plan.color,
                        color: 'white',
                        py: 0.5,
                        px: 2,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <StarIcon fontSize="small" />
                      <Typography variant="body2" fontWeight="medium">
                        {language === 'es' ? 'Recomendado' : 'Recommended'}
                      </Typography>
                    </Box>
                  )}
                  <CardHeader
                    title={plan.title}
                    titleTypographyProps={{ align: 'center', fontWeight: 'bold' }}
                    sx={{ 
                      bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
                      pb: 0
                    }}
                  />
                  <CardContent sx={{ pt: 1, pb: 0 }}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography component="h2" variant="h3" color="text.primary">
                        {displayPrice(plan)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {plan.period}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                      {plan.description}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <List sx={{ mb: 0 }}>
                      {plan.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 35 }}>
                            {feature.included ? (
                              <CheckIcon sx={{ color: plan.color }} />
                            ) : (
                              <CloseIcon color="disabled" />
                            )}
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature.text} 
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions sx={{ mt: 'auto', justifyContent: 'center', p: 2 }}>
                    {(
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        component={RouterLink}
                        to="/register"
                        sx={{
                          borderRadius: 2,
                          py: 1.2,
                          bgcolor: plan.color,
                          '&:hover': {
                            bgcolor: plan.color,
                            opacity: 0.9
                          }
                        }}
                      >
                        {plan.buttonText}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          <PaymentMethodsBadges
            label={language === 'es' ? 'Aceptamos' : 'We accept'}
            sx={{ mt: 5 }}
          />
        </Box>

        {/* Asesoría 1:1 */}
        {(
          <Box sx={{ mb: 8 }}>
            <Grid container justifyContent="center">
              <Grid item xs={12} md={5} sx={{ maxWidth: 450 }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': { transform: 'translateY(-5px)', boxShadow: 5 },
                  }}
                >
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="medium">
                    {personalConsulting.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {personalConsulting.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                    <Typography variant="h4" component="span" color="primary" fontWeight="bold">
                      {convertAndFormat(personalConsulting.priceUSD)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      {personalConsulting.period}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {language === 'es' ? 'Incluye:' : 'Includes:'}
                    </Typography>
                    <List dense disablePadding>
                      {personalConsulting.features.map((feature, idx) => (
                        <ListItem key={idx} disableGutters>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={feature} primaryTypographyProps={{ variant: 'body2' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  <Box sx={{ mt: 'auto', pt: 2 }}>
                    <Button variant="outlined" color="primary" fullWidth component={RouterLink} to="/consultant">
                      {language === 'es' ? 'Programar Consulta' : 'Schedule Consultation'}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Empresas: CapitalOptima + Darwin Optima */}
        {(
          <Box sx={{ mb: 8 }}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 5 },
                textAlign: 'center',
                maxWidth: 800,
                mx: 'auto',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
              }}
            >
              <DarwinOptimaLogo size={36} textColor="white" sx={{ mb: 3 }} />
              <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
                {language === 'es' ? '¿Tienes un negocio?' : 'Do you run a business?'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 600, mx: 'auto', mb: 3 }}>
                {language === 'es'
                  ? 'PCM es para tus finanzas personales. Las finanzas de tu empresa — caja, deudas y estados financieros reales — viven en CapitalOptima, y si tu negocio necesita un reordenamiento más a fondo (operaciones, procesos, equipo), Darwin Optima es la consultoría de un especialista para dueños de negocio que quieren recuperar el control y crecer.'
                  : 'PCM is for your personal finances. Your business finances — cash, debt, and real financial statements — live in CapitalOptima, and if your business needs a deeper reorganization (operations, processes, team), Darwin Optima is a specialist consultancy for business owners who want to regain control and grow.'}
              </Typography>
              <Button
                variant="contained"
                size="large"
                href={darwinOptimaUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  py: 1.2,
                  px: 4,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                {language === 'es' ? 'Conocer Darwin Optima' : 'Visit Darwin Optima'}
              </Button>
            </Paper>
          </Box>
        )}

        {/* FAQ or Additional Info */}
        <Box sx={{ textAlign: 'center', mt: 6, py: 4, bgcolor: 'background.default', borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom>
            {language === 'es' ? '¿Tiene alguna pregunta?' : 'Have questions?'}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {language === 'es' 
              ? 'Nuestro equipo está listo para ayudarle a elegir el mejor plan para sus necesidades.' 
              : 'Our team is ready to help you choose the best plan for your needs.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            href="mailto:augusto@darwinoptima.com"
            sx={{ mt: 2 }}
          >
            {language === 'es' ? 'Contáctenos' : 'Contact Us'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PreciosPage;
